import React, {ReactNode, useState} from 'react';
import Collapse from 'react-bootstrap/Collapse';
import Form from 'react-bootstrap/Form';
import Icon from '@/components/icons/Icon';
import {SelectField} from '@/components/forms';
import FormHelpTooltip from '@/components/utils/FormHelpTooltip';
import {ChartQuery, ChartSettings, DataLabelPosition, BarSortOrder} from 'entities/chart';
import {FormikT} from 'utils/types';
import {chartTimelineIntervals} from '@/views/storyboard/utils/chartConstants';
import {
    showsAnalysisSection,
    showsBignumberOptionsSection,
    showsComparablePeriodInAnalysis,
    showsDataLabelsSetting,
    showsMicrosettingsBlock,
    showsMovingAverageCenterSetting,
    showsMovingAverageOptions,
    showsPeriodSection,
    showsBarSortOrderSetting,
    showsScalingSetting,
} from '@/views/storyboard/utils/chartSettingsFieldVisibility';
import {ChartSettingsBooleanSegmentedControl} from '@/views/storyboard/chart_settings/ChartSettingsSizeControls';
import i18n from 'i18n';


interface Props {
    chartQueryFormik: FormikT<ChartQuery>;
    chartSettingsFormik: FormikT<ChartSettings>;
}

interface OptionRowProps {
    label: ReactNode;
    help?: string;
    extra?: ReactNode;
}

const ChartSettingsOptionRow: React.FC<OptionRowProps> = ({label, help, extra}) => (
    <div className="chart-settings-option-row">
        <div className="chart-settings-option-row-main">
            <span className="chart-settings-option-row-label">
                {label}
                {help ? <FormHelpTooltip>{help}</FormHelpTooltip> : null}
            </span>
        </div>
        {extra ? <div className="chart-settings-option-row-extra">{extra}</div> : null}
    </div>
);

interface ChartSettingsOptionsSectionProps {
    title: string;
    children: ReactNode;
    defaultOpen?: boolean;
    sectionId?: string;
}

export const ChartSettingsOptionsSection: React.FC<ChartSettingsOptionsSectionProps> = ({
    title,
    children,
    defaultOpen = false,
    sectionId,
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const [mounted, setMounted] = useState(defaultOpen);
    const id = sectionId || title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

    const handleToggle = () => {
        setOpen((current) => {
            if (!current) {
                setMounted(true);
            }
            return !current;
        });
    };

    return (
        <div className={`chart-settings-options-section--${id}${open ? '' : ' chart-settings-options-section--collapsed'}`}>
            <button
                type="button"
                className="chart-settings-options-section__title"
                onClick={handleToggle}
                aria-expanded={open}
                aria-controls={`chart-settings-options-section-${id}`}
                data-cy={`chart-settings-options-section-toggle-${id}`}
            >
                <span className="chart-settings-options-section__title-text">
                    {id === 'advanced-settings' ? <Icon name="mdi-cog" size="1em" /> : null}
                    {title}
                </span>
                <Icon name={open ? 'mdi-chevron-down' : 'mdi-chevron-right'} size="1em" />
            </button>
            <Collapse in={open}>
                <div id={`chart-settings-options-section-${id}`}>
                    <div className="chart-settings-options-section__body">
                        {mounted ? children : null}
                    </div>
                </div>
            </Collapse>
        </div>
    );
};

const ChartSettingsOptions: React.FC<Props> = ({
    chartQueryFormik,
    chartSettingsFormik,
}) => {
    const {type: chartType} = chartSettingsFormik.values;
    const {output} = chartQueryFormik.values;
    const showMicrosettings = showsMicrosettingsBlock(chartType, output);

    const activeIntervalLabel = chartTimelineIntervals.find((x) => x[0] === chartQueryFormik.values.intervalUnit)?.[1] || 'day';
    const movingAverageUnit = activeIntervalLabel.toLowerCase();
    const isPieChart = chartType === 'pie';
    const dataLabelOuterOptionLabel = isPieChart
        ? i18n.t('chart_data_labels_outside.text')
        : i18n.t('chart_data_labels_above.text');
    const dataLabelPositionOptions = [
        {
            value: DataLabelPosition.Off,
            label: i18n.t('chart_settings_option_off.text'),
        },
        {
            value: DataLabelPosition.Above,
            label: dataLabelOuterOptionLabel,
        },
        {
            value: DataLabelPosition.Inside,
            label: i18n.t('chart_data_labels_inside.text'),
        },
    ];
    const showMovingAverageOptions = showsMovingAverageOptions(
        chartType,
        output,
        chartSettingsFormik.values.movingAverageDays,
    );
    const showMovingAverageCenter = showsMovingAverageCenterSetting(
        chartType,
        output,
        chartSettingsFormik.values.movingAverageDays,
    );
    const dataLabelsVisible = showsDataLabelsSetting(chartType, output);
    const analysisSectionVisible = showsAnalysisSection(chartType, output);
    const periodSectionVisible = showsPeriodSection(chartType, output);
    const bigNumberOptionsVisible = showsBignumberOptionsSection(chartType);
    const scalingSectionVisible = showMicrosettings && showsScalingSetting(chartType, output);
    const barSortOrderVisible = showsBarSortOrderSetting(chartType, output);
    const barSortOrderOptions = [
        {
            value: BarSortOrder.HighToLow,
            label: i18n.t('chart_settings_bar_sort_high_to_low.text'),
        },
        {
            value: BarSortOrder.LowToHigh,
            label: i18n.t('chart_settings_bar_sort_low_to_high.text'),
        },
    ];
    const movingAverageOptions = [
        {value: null, label: i18n.t('chart_settings_option_off.text')},
        {value: 7, label: `7-${movingAverageUnit}`},
        {value: 14, label: `14-${movingAverageUnit}`},
        {value: 30, label: `30-${movingAverageUnit}`},
    ];
    const periodOptions = chartTimelineIntervals.map(([value, label]) => ({value, label}));
    const activePeriodOption = periodOptions.find(
        (option) => option.value === chartQueryFormik.values.intervalUnit,
    ) || null;
    const activeMovingAverageOption = movingAverageOptions.find(
        (option) => option.value === chartSettingsFormik.values.movingAverageDays,
    ) || (
        chartSettingsFormik.values.movingAverageDays
            ? {
                value: chartSettingsFormik.values.movingAverageDays,
                label: `${chartSettingsFormik.values.movingAverageDays}-${movingAverageUnit}`,
            }
            : movingAverageOptions[0]
    );
    const advancedSelectStyles = {
        control: (base: any) => ({...base, minHeight: 34, height: 34}),
        valueContainer: (base: any) => ({...base, padding: '0 8px'}),
        indicatorsContainer: (base: any) => ({...base, height: 34}),
        menu: (base: any) => ({...base, marginBottom: 8}),
        menuList: (base: any) => ({...base, maxHeight: 180}),
        menuPortal: (base: any) => ({
            ...base,
            zIndex: 2000,
        }),
        option: (base: any, state: any) => ({
            ...base,
            background: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
            backgroundColor: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            ':active': {
                ...base[':active'],
                backgroundColor: 'rgba(0, 158, 251, 0.08)',
            },
        }),
    };
    const advancedSelectProps = {
        compact: true,
        customStyles: advancedSelectStyles,
        maxMenuHeight: 180,
        menuPlacement: 'auto' as const,
        menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
        menuShouldScrollIntoView: true,
    };

    return (
        <div className="chart-settings-options">
            {dataLabelsVisible ? (
                <ChartSettingsOptionRow
                    label={i18n.t('chart_settings_data_labels.text')}
                    help={i18n.t('chart_settings_value_position_help.text')}
                    extra={(
                        <div className="chart-settings-option-control">
                            <SelectField
                                {...advancedSelectProps}
                                id="chart-settings-form-dropdown-data-label-position"
                                name="dataLabelPosition"
                                value={dataLabelPositionOptions.find(
                                    (option) => option.value === chartSettingsFormik.values.dataLabelPosition,
                                ) || null}
                                onChange={(option) => {
                                    if (option) {
                                        chartSettingsFormik.setFieldValue('dataLabelPosition', option.value);
                                    }
                                }}
                                options={dataLabelPositionOptions}
                            />
                        </div>
                    )}
                />
            ) : null}

            {periodSectionVisible ? (
                <ChartSettingsOptionRow
                    label={i18n.t('chart_settings_section_time_period.text')}
                    help={i18n.t('chart_settings_period_help.text')}
                    extra={(
                        <div className="chart-settings-option-control">
                            <SelectField
                                {...advancedSelectProps}
                                name="intervalUnit"
                                value={activePeriodOption}
                                onChange={(option) => {
                                    if (option) {
                                        chartQueryFormik.setFieldValue('intervalUnit', option.value);
                                        chartQueryFormik.setFieldValue('intervalNumber', 1);
                                    }
                                }}
                                options={periodOptions}
                            />
                        </div>
                    )}
                />
            ) : null}

            {showMicrosettings && analysisSectionVisible ? (
                <ChartSettingsOptionRow
                    label={i18n.t('chart_settings_trend_line_regression.text')}
                    help={i18n.t('chart_settings_trendline_help.text')}
                    extra={(
                        <div className="chart-settings-option-control">
                            <ChartSettingsBooleanSegmentedControl
                                value={chartSettingsFormik.values.linearRegression}
                                onChange={(value) => chartSettingsFormik.setFieldValue('linearRegression', value, false)}
                                ariaLabel={i18n.t('chart_settings_trend_line_regression.text')}
                            />
                        </div>
                    )}
                />
            ) : null}

            {showMicrosettings && showMovingAverageOptions ? (
                <ChartSettingsOptionRow
                    label={i18n.t('ch_moving_average.text')}
                    help={i18n.t('chart_settings_moving_average_help.text')}
                    extra={(
                        <div className="chart-settings-option-control">
                            <SelectField
                                {...advancedSelectProps}
                                name="movingAverageDays"
                                value={activeMovingAverageOption}
                                onChange={(option) => chartSettingsFormik.setFieldValue('movingAverageDays', option?.value ?? null, false)}
                                options={movingAverageOptions}
                            />
                        </div>
                    )}
                />
            ) : null}

            {showMicrosettings && showMovingAverageCenter ? (
                <ChartSettingsOptionRow
                    label={i18n.t('chart_settings_moving_average_center.text')}
                    help={i18n.t('chart_settings_moving_average_center_help.text')}
                    extra={(
                        <div className="chart-settings-option-control">
                            <ChartSettingsBooleanSegmentedControl
                                value={chartSettingsFormik.values.movingAverageDaysCenter}
                                onChange={(value) => chartSettingsFormik.setFieldValue('movingAverageDaysCenter', value, false)}
                                ariaLabel={i18n.t('chart_settings_moving_average_center.text')}
                            />
                        </div>
                    )}
                />
            ) : null}

            {(bigNumberOptionsVisible || (
                showMicrosettings
                && analysisSectionVisible
                && showsComparablePeriodInAnalysis(output)
            )) ? (
                <ChartSettingsOptionRow
                    label={i18n.t('es_comparable_period.text')}
                    help={bigNumberOptionsVisible
                        ? i18n.t('chart_settings_comparable_count_help.text')
                        : i18n.t('chart_settings_comparable_period_help.text')}
                    extra={(
                        <div className="chart-settings-option-control">
                            <ChartSettingsBooleanSegmentedControl
                                value={chartSettingsFormik.values.comparablePeriod}
                                onChange={(value) => chartSettingsFormik.setFieldValue('comparablePeriod', value, false)}
                                ariaLabel={i18n.t('es_comparable_period.text')}
                            />
                        </div>
                    )}
                />
            ) : null}

            {barSortOrderVisible ? (
                <ChartSettingsOptionRow
                    label={i18n.t('chart_settings_bar_sort_order.text')}
                    help={i18n.t('chart_settings_bar_sort_order_help.text')}
                    extra={(
                        <div className="chart-settings-option-control">
                            <SelectField
                                {...advancedSelectProps}
                                id="chart-settings-form-dropdown-bar-sort-order"
                                name="barSortOrder"
                                value={barSortOrderOptions.find(
                                    (option) => option.value === chartSettingsFormik.values.barSortOrder,
                                ) || null}
                                onChange={(option) => {
                                    if (option) {
                                        chartSettingsFormik.setFieldValue('barSortOrder', option.value);
                                    }
                                }}
                                options={barSortOrderOptions}
                            />
                        </div>
                    )}
                />
            ) : null}

            {scalingSectionVisible ? (
                <ChartSettingsOptionRow
                    label={i18n.t('jt_scaling_factor.text')}
                    help={i18n.t('chart_settings_scaling_factor_help.text')}
                    extra={(
                        <Form.Control
                            className="chart-settings-option-control"
                            type="text"
                            name="scalingFactor"
                            value={chartSettingsFormik.values.scalingFactor}
                            onChange={chartSettingsFormik.handleChange}
                            onBlur={chartSettingsFormik.handleBlur}
                            isInvalid={
                                chartSettingsFormik.touched.scalingFactor
                                && !!chartSettingsFormik.errors.scalingFactor
                            }
                        />
                    )}
                />
            ) : null}

        </div>
    );
};

export default React.memo(ChartSettingsOptions);
